import { isAdrAvailable } from '@backstage-community/plugin-adr';
import {
  EntityConsumedApisCard,
  EntityProvidedApisCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAzureGitTagsContent,
  EntityAzurePullRequestsContent,
  isAzureDevOpsAvailable,
} from '@backstage-community/plugin-azure-devops';
import {
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityLayout,
  EntitySwitch,
  isComponentType,
} from '@backstage/plugin-catalog';
import { EntitySonarQubeCard } from '@backstage-community/plugin-sonarqube';
import { isSonarQubeAvailable } from '@backstage-community/plugin-sonarqube-react';
import { Grid } from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';

import ADRList from '../../../pages/Adr';
import { CustomDomainEntityLayout } from '../../../pages/EntityLayout/CustomDomainEntityLayout';
import { cicdContent } from '../content/cicdContent';
import { techdocsContent } from '../content/techdocsContent';
import { useEntity } from '@backstage/plugin-catalog-react';
import CheckMarx from '../../../pages/Checkmarx';
import {
  fetchData,
  urls,
} from '@internal/backstage-plugin-dh-dock-shared-common';
import { Entity } from '@backstage/catalog-model';
import { useDispatch } from 'react-redux';
import { setIsBusinessComponent } from '../../../redux/slices/globalVariableSlice';
import { contentpages, notification } from '../../../utils/constants';

export const myCustomFilterFn = (path: string): boolean => {
  if (path === 'template.md' || path === 'index.md' || path === 'README.md') {
    return false;
  }
  // Match all files following the pattern title-with-dashes.md
  return /^.+\.md$/.test(path);
};
const isSCheckmarxAvailable = () => {
  const { entity } = useEntity();
  const project = entity?.metadata?.annotations?.['checkmarx.com/project'];
  return !!project;
};

export const isBusinessComponent = async (): Promise<boolean> => {
  const id = window.location?.pathname?.split('/')[4];
  if (!id) return false;

  try {
    const response = (await fetchData(
      `${urls.getComponentDetailsId}${id}`,
    )) as Entity;

    const componentTypes = [notification, contentpages] as const;

    // Check if the entity type matches any of the component types
    const isBusiness = componentTypes.some(
      type => response.spec?.type?.toString()?.toLowerCase() === type,
    );

    return isBusiness;
  } catch (error) {
    console.error('Error fetching entity:', error);
    return false;
  }
};

const ServiceEntityPage: FC<{ overviewContent: JSX.Element }> = ({
  overviewContent,
}) => {
  const [hasBusinessComponent, setHasBusinessComponent] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const isBusinessComponent_ = async () => {
      const response = await isBusinessComponent();
      dispatch(setIsBusinessComponent(response));

      setHasBusinessComponent(response);
    };
    isBusinessComponent_();
  }, [dispatch]);

  return (
    <>
      <CustomDomainEntityLayout>
        <EntityLayout.Route path="/" title="Overview">
          {overviewContent}
        </EntityLayout.Route>
        {!hasBusinessComponent && (
          <>
            <EntityLayout.Route path="/ci-cd" title="CI/CD">
              {cicdContent(isAzureDevOpsAvailable)}
            </EntityLayout.Route>

            <EntityLayout.Route
              if={isSonarQubeAvailable}
              path="/sonarqube"
              title="SonarQube"
            >
              <EntitySonarQubeCard variant="gridItem" />
            </EntityLayout.Route>
            <EntityLayout.Route
              if={isSCheckmarxAvailable}
              path="/checkmarx"
              title="Checkmarx"
            >
              <CheckMarx />
            </EntityLayout.Route>

            <EntityLayout.Route
              if={isAzureDevOpsAvailable}
              path="/pull-requests"
              title="Pull Requests"
            >
              <EntityAzurePullRequestsContent defaultLimit={25} />
            </EntityLayout.Route>

            <EntityLayout.Route
              if={isAzureDevOpsAvailable}
              path="/git-tags"
              title="Git Tags"
            >
              <EntityAzureGitTagsContent />
            </EntityLayout.Route>

            <EntityLayout.Route path="/api" title="API">
              <Grid>
                <Grid item md={12}>
                  <EntityProvidedApisCard />
                </Grid>
                <br />
                <Grid item md={12}>
                  <EntityConsumedApisCard />
                </Grid>
              </Grid>
            </EntityLayout.Route>

            <EntityLayout.Route path="/dependencies" title="Dependencies">
              <Grid container spacing={3} alignItems="stretch">
                <Grid item md={6}>
                  <EntityDependsOnComponentsCard />
                </Grid>
                <Grid item md={6}>
                  <EntityDependsOnResourcesCard />
                </Grid>
              </Grid>
            </EntityLayout.Route>

            <EntityLayout.Route path="/docs" title="Docs">
              {techdocsContent}
            </EntityLayout.Route>

            <EntityLayout.Route if={isAdrAvailable} path="/adr" title="ADR">
              <>
                <ADRList />
              </>
            </EntityLayout.Route>
          </>
        )}
      </CustomDomainEntityLayout>
    </>
  );
};

const websiteEntityPage = (overviewContent: JSX.Element) => (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent(isAzureDevOpsAvailable)}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isSonarQubeAvailable}
      path="/sonarqube"
      title="SonarQube"
    >
      <EntitySonarQubeCard variant="gridItem" />
    </EntityLayout.Route>
    <EntityLayout.Route
      if={isSCheckmarxAvailable}
      path="/checkmarx"
      title="Checkmarx"
    >
      <CheckMarx />
    </EntityLayout.Route>
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isAdrAvailable} path="/adr" title="ADR">
      <>
        <ADRList />
      </>
    </EntityLayout.Route>
  </EntityLayout>
);

export const componentPage = (overviewContent: JSX.Element) => (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      <ServiceEntityPage overviewContent={overviewContent} />
    </EntitySwitch.Case>

    <EntitySwitch.Case
      if={isComponentType('website') || isComponentType('library')}
    >
      {websiteEntityPage(overviewContent)}
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      {<ServiceEntityPage overviewContent={overviewContent} />}
    </EntitySwitch.Case>
  </EntitySwitch>
);
